import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'
import { AdviceDataTable } from '../../../../components'
import { MOBILE_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { StepContent } from '../../elements'
import { getHeadingSegments } from './headingMessage'
import { allSsiExcluded, buildColumns, buildRows } from './tableData'
import DontShowAgain from './DontShowAgain'
import { Heading, Subheading, MobileDismissRow, TableWrap } from './style'

const BEFORE_TAX_NOTE = 'Please note: Social Security estimates are before-tax.'

// Renders the heading segments, showing the age(s) in bold.
function HeadingText({ segments }) {
  return segments.map((segment, index) =>
    segment.bold ? <strong key={index}>{segment.text}</strong> : segment.text
  )
}

/**
 * SSI step of the step-by-step advice flow. Unlike the other steps this one
 * makes no recommendation — it reflects the user's current Social Security
 * configuration: a heading describing the setup, an optional before-tax note,
 * and a table of each included person's start age / monthly amount / status.
 *
 * When everyone in planning has SSI excluded, a "don't show me again" checkbox
 * appears (in the footer on desktop, here between the heading and table on
 * mobile). `onDismiss` records the preference and advances past the step.
 */
const SSI = ({ store, onDismiss, dismissDisabled }) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const { person, spouse, primarySSIBenefits, spouseSSIBenefits, userSettings } = store

  // The shell shows the loader; this just guards a transient render before SSI
  // benefits resolve.
  if (!primarySSIBenefits) {
    return null
  }

  const spouseIncludedInPlanning = !!store.includeSpouseInPlanning
  const spouseName = spouse?.firstName

  const headingSegments = getHeadingSegments({
    spouseIncludedInPlanning,
    primary: primarySSIBenefits,
    spouse: spouseSSIBenefits,
    spouseName,
  })

  const excludedAll = allSsiExcluded({
    spouseIncludedInPlanning,
    primary: primarySSIBenefits,
    spouse: spouseSSIBenefits,
  })

  const columns = buildColumns({ spouseIncludedInPlanning })
  const rows = buildRows({
    spouseIncludedInPlanning,
    personName: person?.displayName,
    spouseName,
    primary: primarySSIBenefits,
    spouse: spouseSSIBenefits,
  })

  // The checkbox only advances the flow; if there's no dismiss handler wired
  // (e.g. no next step yet) fall back to just recording the preference.
  const handleDismiss = onDismiss ?? (() => store.setHideSsiAdviceStep(true))

  return (
    <StepContent>
      <Heading>
        <HeadingText segments={headingSegments} />
      </Heading>
      {!excludedAll && <Subheading>{BEFORE_TAX_NOTE}</Subheading>}

      {excludedAll && isMobile && (
        <MobileDismissRow>
          <DontShowAgain
            checked={!!userSettings?.hideSsiAdviceStep}
            onConfirm={handleDismiss}
            disabled={dismissDisabled}
          />
        </MobileDismissRow>
      )}

      <TableWrap>
        <AdviceDataTable columns={columns} rows={rows} />
      </TableWrap>
    </StepContent>
  )
}

SSI.propTypes = {
  /** Records the "don't show me again" preference and advances past this step. */
  onDismiss: PropTypes.func,
  /** Block the dismiss control, which navigates (see DontShowAgain). */
  dismissDisabled: PropTypes.bool,
}

export default inject('store')(observer(SSI))
